

























































































import { Vue, Component, PropSync, Ref } from 'vue-property-decorator'
import { Inject } from 'inversify-props'
import CampoDecimal from '@/components/ui/CampoDecimal.vue'
import { ItemDaVenda, ItemNotaFiscal, NotaFiscalCompleta } from '@/models'
import { obrigatorio } from '@/shareds/regras-de-form'
import { formatarMoeda } from '@/shareds/formatadores'
import { VendaModule } from '@/store/vuex/venda/VendaStore'
import AlertModule from '@/store/vuex/aplicacao/AlertModule'
import type { NotaFiscalServiceAdapter } from '@/usecases'

@Component({
	components: {
		CampoDecimal,
	},
})
export default class NotaDeCreditoReducaoView extends Vue {
	@PropSync('mostra', { type: Boolean, default: false }) syncMostra!: boolean
	@Ref() form!: HTMLFormElement

	@Inject('NotaFiscalServiceAdapter')
	private notaFiscalService!: NotaFiscalServiceAdapter

	obrigatorio = obrigatorio
	formatarMoeda = formatarMoeda

	chave = ''
	notaDeOrigem: NotaFiscalCompleta | null = null
	reducoes: Record<string, number> = {}
	infoAdicionais = ''
	buscando = false

	headers = [
		{ text: 'Item', value: 'nitem', sortable: false, width: 60 },
		{ text: 'Produto', value: 'produto', sortable: false },
		{ text: 'Quantidade', value: 'qcom', sortable: false, width: 100 },
		{ text: 'Valor na nota', value: 'valorDoItem', sortable: false, width: 130 },
		{ text: 'Valor da redução', value: 'reducao', sortable: false, width: 170 },
	]

	get chaveSemFormatacao() {
		return (this.chave || '').replace(/\D/g, '')
	}

	get totalDaReducao() {
		if (!this.notaDeOrigem) return 0
		return this.notaDeOrigem.det.reduce((total, item) => total + (Number(this.reducoes[item.id]) || 0), 0)
	}

	valorDoItem(item: ItemNotaFiscal) {
		return (Number(item.vprod) || 0) - (Number(item.vdesc) || 0)
	}

	// O CampoDecimal passa para as regras o texto formatado ("1.000,00"), não o número
	validarReducao(valor: number | string | null, item: ItemNotaFiscal) {
		const numero = typeof valor === 'string'
			? Number(valor.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.'))
			: valor
		if (!numero) return true
		if (numero < 0) return 'Informe um valor positivo'
		if (numero > this.valorDoItem(item) + 0.001) return 'Maior que o valor do item na nota'
		return true
	}

	async buscarNotaDeOrigem() {
		if (this.chaveSemFormatacao.length !== 44) {
			AlertModule.setError('Informe a chave de acesso da NF-e de origem, com 44 dígitos.')
			return
		}

		try {
			this.buscando = true
			const notaDeOrigem = await this.notaFiscalService.buscarOrigemDaReducaoDeValores(this.chaveSemFormatacao)

			// A nota de crédito referencia nota do mesmo emitente (NT 2025.002, regra B25-50)
			const cnpjDaLoja = (VendaModule.lojaDaEntrada?.cnpj || '').replace(/\D/g, '')
			if (cnpjDaLoja && (notaDeOrigem.cnpjemit || '').replace(/\D/g, '') !== cnpjDaLoja) {
				this.notaDeOrigem = null
				AlertModule.setError('A NF-e de origem foi emitida por outra loja. Selecione a loja emissora da nota original.')
				return
			}

			this.notaDeOrigem = notaDeOrigem
			this.reducoes = notaDeOrigem.det.reduce((reducoes, item) => ({ ...reducoes, [item.id]: 0 }), {})
		} catch (error) {
			this.notaDeOrigem = null
			AlertModule.setError(error)
		} finally {
			this.buscando = false
		}
	}

	concluir() {
		if (!this.form.validate() || !this.notaDeOrigem || !VendaModule.emissaoEntradaAtual) return

		const notaDeOrigem = this.notaDeOrigem
		const itensReduzidos = notaDeOrigem.det.filter(item => (Number(this.reducoes[item.id]) || 0) > 0)
		if (!itensReduzidos.length) {
			AlertModule.setError('Informe o valor da redução em pelo menos um item da nota de origem.')
			return
		}

		const venda = VendaModule.emissaoEntradaAtual
		venda.itens = itensReduzidos.map(item => criarItemDaReducao(item, Number(this.reducoes[item.id]), notaDeOrigem.chNFe))
		venda.tipoDeTransacao = 'Complementar'
		venda.tipoNotaCredito = '04'
		venda.tipoNota = 'Entrada'
		venda.infoAdicionais = this.infoAdicionais.trim() || null

		this.$emit('concluir', notaDeOrigem.chNFe)
		this.limpar()
		this.syncMostra = false
	}

	cancelar() {
		this.limpar()
		this.$emit('update:mostra', false)
	}

	limpar() {
		this.form.resetValidation()
		this.chave = ''
		this.notaDeOrigem = null
		this.reducoes = {}
		this.infoAdicionais = ''
	}
}

// Um item por item da NF-e de origem: quantidade 1 e o valor da redução no preço; o backend monta o XML com os dados
// do item de origem e calcula o IBS/CBS com as alíquotas dele
function criarItemDaReducao(item: ItemNotaFiscal, valorDaReducao: number, chaveDaOrigem: string): ItemDaVenda {
	return {
		id: '',
		produto: item.produto,
		desconto: { isPercentual: true, valor: 0 },
		preco: Number(valorDaReducao.toFixed(2)),
		descontoVarejo: null,
		quantidade: 1,
		quantidadeLida: 0,
		precoDeCusto: 0,
		idConjunto: null,
		percentualCashback: 0,
		diasParaEfetivarCashback: 0,
		isBrinde: false,
		idItemNotaOrigem: item.id,
		chaveNotaOrigem: chaveDaOrigem,
		diasParaExpirarCashback: 0,
		possuiDescontoPorTabela: false,
		valorDescontoRateadoDoItem: 0,
		itemDeDevolucao: false,
		isTotalmenteTrocado: false,
		motivoCancelamento: null,
		isCompra: false,
		idItemOrigem: '',
		isDaVendaReaberta: false,
		vendedor: null,
		autenticadoParaLiberarProdutoSemEstoque: false,
		vendaOrigemDoItem: null,
		valorFreteRateado: 0,
		isItemReferenteATroca: false,
		isDeOutraLoja: false,
		percentualDescontoTabelaPreco: 0,
		precoDeCustoReal: 0,
	} as ItemDaVenda
}
